import React from 'react';
import classnames from 'classnames';
import { Badge } from '@cargomatic/ui';
import _ from 'underscore';
import { titleize, humanize } from 'underscore.string';
import i18next from 'i18next';
import unitConverter from 'convert-units';
import { sluggify } from 'utils/localization/utils';
import {
  DEFAULT_DIMENSIONAL_UNIT,
  DEFAULT_DISTANCE_UNIT,
  DEFAULT_WEIGHT_UNIT,
} from 'utils/localization/types';
import EditButton from 'components/v2/buttons/EditButton';
import { getOpentrackTrackingInfo } from 'utils/opentrack-utils';
import { Check, X } from 'lucide-react';
import ExternalLink from 'components/v2/ExternalLink';

const localeCode = LOCALE_CODE;

export const portIntegrationsFormatter = (cell) => {
  if (!cell.value || cell.value === '') return null;
  return (
    <div data-testid="div-f4bd7171" className="">
      <span
        data-testid="span-6f50b119"
        className="justify-center p-1 text-white rounded bg-blue-light"
      >
        {titleize(humanize(i18next.t(sluggify(cell.value))))}
      </span>
    </div>
  );
};

export const opentrackTrackingStatusFormatter = (cell) => {
  if (!cell.value || cell.value === '') return null;
  const { localizationSlug, badgeColor } = getOpentrackTrackingInfo(cell.value);
  return (
    <div data-testid="div-c5ea5ef7" className="">
      <Badge dataTestId="Badge-7f3d545c" className="mt-1" color={badgeColor}>
        {i18next.t(localizationSlug)}
      </Badge>
    </div>
  );
};

export const masterBolStatusFormatter = (cell) => {
  if (!cell.value || cell.value === '') return null;
  const badgeColor = cell.value === 'confirmed' ? 'green' : 'red';
  return (
    <div data-testid="div-585142f2" className="">
      <Badge dataTestId="Badge-ff194360" className="mt-1" color={badgeColor}>
        {i18next.t(cell.value.toUpperCase())}
      </Badge>
    </div>
  );
};

export const statusFormatter = (cell) => {
  if (!cell.value) return null;
  return (
    <div data-testid="div-43c2669b" className="">
      <Badge dataTestId="Badge-eeb1695f" color="brand">
        {titleize(humanize(i18next.t(sluggify(cell.value))))}
      </Badge>
    </div>
  );
};

export const lineSeparatedStringFormatter = (cell) => {
  if (!cell.value || cell.value === '') {
    return null;
  }
  const holds = cell.value.split('\n');

  return (
    <div data-testid="div-61e16320">
      {holds.map((hold, i) => {
        return (
          <div data-testid={`div-efe980f6-${i}`} key={i}>
            <span data-testid={`span-9f014aca-${i}`}>
              {i18next.t(sluggify(hold))}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const checkedFormatter = ({ value }) => {
  if (typeof value === 'boolean' && value) {
    return (
      <div data-testid="div-f97840dc" className="my-2">
        <Check className="text-green-500 h-4 w-4" />
      </div>
    );
  }
  if (typeof value === 'boolean' && !value) {
    return (
      <div data-testid="div-550f1791" className="my-2">
        <X className="text-red-500 h-4 w-4" />
      </div>
    );
  }

  return '';
};

export const tagFormatter = (cell) => {
  const items = typeof cell.value === 'string' ? [cell.value] : cell.value; // cutover from string to array

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    cell.api.startEditingCell({
      rowIndex: cell.node.rowIndex,
      colKey: cell.column.getId(),
    });
  };

  return (
    <div
      data-testid="div-424a4cb9"
      className="flex flex-wrap flex-row overflow-visible pt-0.5"
    >
      <div
        data-testid="div-2753f65e"
        className="flex flex-col flex-1 overflow-hidden"
      >
        {cell.value &&
          items.map((i, index) => {
            const val = i.trim();
            return (
              <div
                data-testid={`div-3cf3787c-${index}`}
                key={val}
                className="justify-center px-1 mb-1 mr-1 text-white rounded bg-grey-dark inline-block flex-1 whitespace-nowrap w-fit max-w-36 truncate"
              >
                {val}
              </div>
            );
          })}
      </div>
      <div data-testid="div-06d036b5" className="w-8">
        <EditButton
          dataTestId="EditButton-70f44192"
          size="small"
          onClick={handleClick}
        />
      </div>
    </div>
  );
};

export const orderIDRenderer = (props) => {
  return (
    <div
      data-testid="div-8eb78023"
      style={{ zIndex: 1000000 }}
      className="flex items-center space-x-4"
    >
      <button
        data-testid="button-bc33ef71"
        style={{ color: 'DodgerBlue' }}
        onClick={() => props.onOrderClick(props.data)}
      >
        {' '}
        {props.value}{' '}
      </button>

      {props.data?.order_oid && (
        <ExternalLink
          dataTestId={`ExternalLink-${props.data.order_oid}`}
          href={`/orders/${props.data.order_oid}`}
          title={''}
          target="_blank"
          rel="noreferrer"
          showIcon
        />
      )}
    </div>
  );
};

export const shipmentIDRenderer = (props) => {
  return (
    <div data-testid="div-47fe4287" className="flex items-center space-x-4">
      <button
        data-testid="button-c7fdb771"
        style={{ color: 'DodgerBlue' }}
        onClick={() => props.onShipmentClick(props.data)}
      >
        {' '}
        {props.value}{' '}
      </button>

      {props.data?.shipment_oid && (
        <ExternalLink
          dataTestId={`ExternalLink-${props?.data?.shipment_oid}`}
          href={`/shipments/${props?.data?.shipment_oid}`}
          title={''}
          target="_blank"
          rel="noreferrer"
          showIcon
        />
      )}
    </div>
  );
};

export const shipperShipmentIDRenderer = (props) => {
  return (
    <div data-testid="div-295e512f">
      <a
        data-testid="a-9932e27c"
        style={{ color: 'DodgerBlue' }}
        href={`/shipments/${props.data.shipment_oid}`}
        target="_blank"
        rel="noreferrer"
      >
        {' '}
        {props.value}{' '}
      </a>
    </div>
  );
};

export const marginFormatter = (props) => {
  if (props.value === '' || props.value === null) return null;

  const value = props.value;
  const containerClassNames = classnames({
    'p-1': true,
    'justify-center': true,
    'text-white': true,
    rounded: true,
    'bg-red-500': value <= 0,
    'bg-yellow-400': value > 0 && value <= 15,
    'bg-green': value > 15,
  });
  return (
    <div data-testid="div-f946631e">
      <span data-testid="span-d22ec73c" className={containerClassNames}>
        &nbsp;{value}%&nbsp;
      </span>
    </div>
  );
};

export const deliveryOrderFormatter = (cell) => {
  if (_.isEmpty(cell.data)) return null;
  const value = cell.value ? cell.value : 'not-sent';
  return (
    <span data-testid="span-5c5c4847">
      {humanize(i18next.t(sluggify(value)))}
    </span>
  );
};

export const LoadDimensionFormatter = (props) => {
  const dimensional_units = props.dimensional_units || DEFAULT_DIMENSIONAL_UNIT;
  if (props.quantity && props.type) {
    return (
      <span data-testid="span-fb97d9cc">{`${props.quantity} ${titleize(
        props.type,
      )} (${props.length}x${props.width}x${
        props.height
      } ${dimensional_units})`}</span>
    );
  }
  return (
    <span data-testid="span-b01a2cca">{`(${props.length}x${props.width}x${props.height} ${dimensional_units})`}</span>
  );
};

export const LoadUnitFormatter = (props) => {
  const weight_units = props.weight_units || DEFAULT_WEIGHT_UNIT;
  if (props.isTotalWeight) {
    return (
      <span data-testid="span-d1bd4c04">
        {i18next.t('TOTAL_WEIGHT_WITH_VALUE_AND_UNIT', {
          valueAndUnit: `${props.weight} ${weight_units}`,
        })}
      </span>
    );
  }
  return (
    <span data-testid="span-92d7e1d1">
      {props.weight} {weight_units}
    </span>
  );
};

export const DistanceUnitFormatter = (props) => {
  const toUnit = props.toUnit || DEFAULT_DISTANCE_UNIT;
  if (
    unitConverter().possibilities('length').includes(props.fromUnit) &&
    unitConverter().possibilities('length').includes(toUnit)
  ) {
    const formattedDistance = unitConverter(Number(props.distance))
      .from(props.fromUnit)
      .to(toUnit)
      .toFixed(2);
    return (
      <span data-testid="span-d8031417">
        {formattedDistance} {toUnit}
      </span>
    );
  }
  return (
    <span data-testid="span-24646d04">
      {Number(props.distance).toFixed(2)} {toUnit}
    </span>
  );
};

export const getCurrencySymbol = (currency) =>
  new Intl.NumberFormat(localeCode, { style: 'currency', currency })
    .formatToParts(1)
    .find((x) => x.type === 'currency').value;
